import React, { useState } from 'react';
import colors from 'config/colors';
import { splitLinks, classifyLink, extractLinks } from 'utils/linkUtils';

const linkStyle = { color: colors.aTag, wordBreak: 'break-all', textDecoration: 'underline' };

// Text with its links clickable (opens in a new tab). Line breaks in the text are kept.
export const LinkifiedText = ({ text, style }) => (
  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...style }}>
    {splitLinks(text).map((part, i) =>
      part.type === 'link' ? (
        <a key={i} href={part.href} target="_blank" rel="noopener noreferrer" style={linkStyle}>
          {part.value}
        </a>
      ) : (
        <React.Fragment key={i}>{part.value}</React.Fragment>
      )
    )}
  </span>
);

const frameStyle = { width: '100%', border: 0, borderRadius: 8, backgroundColor: '#000' };

const OpenLink = ({ href, label }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" style={{ ...linkStyle, fontSize: 12, fontWeight: 'bold' }}>
    {label}
  </a>
);

// What a link points to: YouTube plays inline, images and video files show in place, Instagram / Facebook posts open
// as a preview on request (they load third-party scripts, so not until asked), anything else is a link card.
export const LinkPreview = ({ href }) => {
  const info = classifyLink(href);
  const [showEmbed, setShowEmbed] = useState(false);

  if (info.kind === 'youtube') {
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ position: 'relative', paddingTop: '56.25%' }}>
          <iframe
            title={info.label}
            src={info.embedUrl}
            loading="lazy"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ ...frameStyle, position: 'absolute', top: 0, left: 0, height: '100%' }}
          />
        </div>
        <OpenLink href={href} label="Open on YouTube" />
      </div>
    );
  }
  if (info.kind === 'image') {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: 8 }}>
        <img src={href} alt="Linked" loading="lazy" style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 8 }} />
      </a>
    );
  }
  if (info.kind === 'video') {
    return <video controls preload="metadata" src={href} style={{ ...frameStyle, marginTop: 8, maxHeight: 360 }} />;
  }

  const isSocial = info.kind === 'instagram' || info.kind === 'facebook';
  return (
    <div
      style={{
        marginTop: 8,
        border: '1px solid #e3e8ee',
        borderRadius: 8,
        padding: '8px 12px',
        backgroundColor: '#fafbfc',
        maxWidth: 520,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'bold', fontSize: 13, color: colors.grey1 }}>
          {isSocial ? info.label : info.host}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
          {isSocial && info.embedUrl && (
            <a
              href="#preview"
              style={{ ...linkStyle, fontSize: 12, fontWeight: 'bold', textDecoration: 'none' }}
              onClick={e => {
                e.preventDefault();
                setShowEmbed(v => !v);
              }}>
              {showEmbed ? 'Hide preview' : 'Show preview'}
            </a>
          )}
          <OpenLink
            href={href}
            label={`Open${isSocial ? ` on ${info.kind === 'instagram' ? 'Instagram' : 'Facebook'}` : ''}`}
          />
        </span>
      </div>
      {showEmbed && (
        <iframe
          title={info.label}
          src={info.embedUrl}
          loading="lazy"
          allowFullScreen
          style={{
            ...frameStyle,
            marginTop: 8,
            height: info.kind === 'instagram' ? 560 : 420,
            backgroundColor: '#fff',
          }}
        />
      )}
    </div>
  );
};

// A circular / notice / post body: the text with links clickable and, when `embed` is set, a preview under it for each
// link (at most `maxPreviews`, the rest stay plain links). `embed` is left off where space is tight (dashboard cards).
const RichText = ({ text, embed = false, maxPreviews = 2, style }) => {
  const links = embed ? [...new Set(extractLinks(text).map(link => link.href))].slice(0, maxPreviews) : [];
  return (
    <div style={style}>
      <LinkifiedText text={text} />
      {links.map(href => (
        <LinkPreview key={href} href={href} />
      ))}
    </div>
  );
};

export default RichText;
